/* ============================================================================
   Ohr Labs — Grid Snap (grid-snap.js)  ·  the ONE layout-alignment mechanism
   ----------------------------------------------------------------------------
   Mark elements with data-attributes; this snaps them to the --cell grid on
   load / fonts-ready / resize-settle (never on scroll). One system, everything.

     [data-g-block]        top-level band → snap TOP to a line + round HEIGHT to
                           whole cells (min-height). Cascades in document order.

     [data-g-fit]          box (button, input, selector, framed box) → snap its
                           LEFT and RIGHT edges to grid lines so it always fills a
                           whole number of cells, never floating between lines.
     [data-g-inset="N"]    on a [data-g-fit], hold N cells clear inside each side
                           edge of its parent block (default 0). e.g. inset="1"
                           = one empty cell of margin left & right.

     .drawer-region /      bottom-pinned drawer blocks → snap their BOTTOM edge up onto a
     .drawer-cta           grid line (they are inside a position:fixed drawer whose grid
                           tiles from the viewport top). Keeps the block on grid rows with
                           a 1–2 cell gap from the bottom; label+select stay glued.

   Uses margins / min-height / width (not the section paddings) so section
   !important CSS can't override it. Reads then writes per element; blocks run
   sequentially so the vertical cascade stays consistent. Degrades gracefully.
   ============================================================================ */
(function () {
  var raf = 0;

  function cellPx() {
    var n = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--cell'));
    return (n && n > 0) ? n : 40;
  }

  // Snap a control's VERTICAL position to the grid by its own coordinate:
  // top edge to a line + height rounded to whole cells. Horizontal is handled
  // structurally (whole-cell section gutters + full-width elements) — we do NOT
  // touch width/left here (doing so overflowed the viewport on resize).
  function fitBox(el, cell) {
    el.style.removeProperty('margin-top');
    el.style.removeProperty('min-height');
    el.style.removeProperty('margin-left');
    var r = el.getBoundingClientRect();
    var top = r.top + window.scrollY;
    var topOff = ((top % cell) + cell) % cell;
    if (topOff > 0.5 && topOff < cell - 0.5) el.style.setProperty('margin-top', (cell - topOff) + 'px', 'important');
    var h = el.getBoundingClientRect().height;
    var rounded = Math.ceil((h - 0.5) / cell) * cell;
    if (rounded - h > 0.5) el.style.setProperty('min-height', rounded + 'px', 'important');
    // Optional HORIZONTAL snap (opt-in via [data-g-fit-x]): nudge the LEFT edge onto the
    // nearest grid line so a whole-cell-wide box sits flush in its column. Desktop-only,
    // so full-width mobile controls are never shifted.
    if (el.hasAttribute('data-g-fit-x') && window.innerWidth >= 990) {
      var left = el.getBoundingClientRect().left + window.scrollX;
      var leftOff = ((left % cell) + cell) % cell;
      var adjust = leftOff <= cell / 2 ? -leftOff : (cell - leftOff);
      if (Math.abs(adjust) > 0.5) el.style.setProperty('margin-left', adjust + 'px', 'important');
    }
  }

  // Bottom-pinned drawer block: nudge its BOTTOM edge UP onto the nearest grid line. The
  // drawer is position:fixed (top:0) so its grid tiles from the viewport top and bottom-edge
  // coordinates are grid-relative even mid-slide (the drawer's translateX doesn't affect Y).
  // Grow the block's bottom margin so its bottom edge lands on a grid line: it keeps its base
  // 1-cell margin plus up to <1 more cell, so the gap from the bottom settles between 1 and 2
  // whole cells and the block lands on grid rows. (margin, not transform — the .btn CTA has a
  // transform transition that would otherwise swallow the change.)
  function snapBottom(el, cell) {
    el.style.removeProperty('margin-bottom');
    var bottom = el.getBoundingClientRect().bottom;
    var off = ((bottom % cell) + cell) % cell;
    if (off > 0.5) el.style.marginBottom = (cell + off) + 'px';
  }

  function snap() {
    var cell = cellPx();

    // 1) VERTICAL — blocks, in document order (cascade)
    var blocks = document.querySelectorAll('[data-g-block]');
    var i, el, top, topOff, h, rounded;
    for (i = 0; i < blocks.length; i++) { blocks[i].style.marginTop = ''; blocks[i].style.minHeight = ''; }
    for (i = 0; i < blocks.length; i++) {
      el = blocks[i];
      top = el.getBoundingClientRect().top + window.scrollY;
      topOff = ((top % cell) + cell) % cell;
      if (topOff > 0.5 && topOff < cell - 0.5) el.style.marginTop = (cell - topOff) + 'px';
      h = el.getBoundingClientRect().height;
      rounded = Math.ceil((h - 0.5) / cell) * cell;
      if (rounded - h > 0.5) el.style.minHeight = rounded + 'px';
    }

    // 2) GLOBAL LOCATION — every content control snaps its own box to grid lines
    //    by absolute coordinate (not reliant on the stack above it).
    var fits = document.querySelectorAll(
      '.hero-ctas .btn, .form-row input, .form-row .btn, ' +
      '.stat .cta .btn, .south-london .cta .btn, [data-g-fit]'
    );
    var vw = window.innerWidth;
    for (i = 0; i < fits.length; i++) {
      if (fits[i].offsetParent === null) continue;   // skip hidden (closed drawers)
      var mode = fits[i].getAttribute('data-g-fit');  // "desktop" / "mobile" / absent(=always)
      // Wrong-mode registrations must be CLEARED (not just skipped): a min-height/margin
      // set at desktop width would otherwise persist as a stale fixed height at tablet/mobile.
      if ((mode === 'desktop' && vw < 990) || (mode === 'mobile' && vw >= 990)) {
        fits[i].style.removeProperty('margin-top');
        fits[i].style.removeProperty('min-height');
        fits[i].style.removeProperty('margin-left');
        continue;
      }
      fitBox(fits[i], cell);
    }

    // 3) BOTTOM-PINNED DRAWER BLOCKS — snap their bottom edge onto a grid line.
    var bottoms = document.querySelectorAll('.drawer-region, .drawer-cta');
    for (i = 0; i < bottoms.length; i++) {
      if (bottoms[i].offsetParent === null) continue; // skip hidden (closed drawers)
      snapBottom(bottoms[i], cell);
    }

    // 3b) CART-DRAWER FOOTER — bottom-anchored to the viewport (100dvh is rarely a
    //     whole number of cells), so its rows' grid alignment was device-dependent.
    //     Nudge just its BOTTOM edge up onto the nearest grid line (plain offset, no
    //     extra base cell like snapBottom's menu styling); every row inside is a
    //     whole-cell height, so they all land on grid lines on any device.
    var dfoot = document.querySelector('.dcart-foot');
    if (dfoot && dfoot.offsetParent !== null) {
      dfoot.style.removeProperty('margin-bottom');
      var db = dfoot.getBoundingClientRect().bottom;
      var doff = ((db % cell) + cell) % cell;
      if (doff > 0.5 && doff < cell - 0.5) dfoot.style.marginBottom = doff + 'px';
    }

    // 4) After heights settle (min-heights added above grow the page), redraw the
    //    full-page grid so grid lines always reach the very bottom.
    if (window.drawGrid) window.drawGrid();
  }

  function schedule() { cancelAnimationFrame(raf); raf = requestAnimationFrame(snap); }

  // Public: call after the --cell quantiser changes the cell, or after opening a drawer.
  window.gridSnap = snap;

  // First snap ASAP — this script is deferred, so it executes right after HTML
  // parsing (before first paint on most loads). Waiting for window 'load' here
  // made the un-snapped layout paint first, then visibly jump onto the grid once
  // all images finished. Images carry width/height attrs so early measurement is
  // correct; the load/fonts re-snaps below still catch any late drift.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  window.addEventListener('load', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

  var t;
  window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(snap, 140); });
})();
